import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ThemeService } from '../../core/theme/theme.service';

/**
 * Interruptor de tema. Un solo boton, dos estados visibles: el sol y la luna
 * conviven en el mismo cuadro y se cruzan girando, asi que no hay salto de
 * layout ni parpadeo de icono al alternar.
 *
 * La etiqueta accesible nombra la accion destino, no el estado actual: un
 * lector de pantalla anuncia "Activar modo oscuro", que es lo que pasara al
 * pulsar, y no "Modo claro", que se lee como una afirmacion.
 */
@Component({
  selector: 'lc-theme-toggle',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="tt"
      [attr.aria-label]="label()"
      [attr.title]="label()"
      (click)="theme.toggle()"
    >
      <span class="tt__icons" [class.tt__icons--dark]="isDark()" aria-hidden="true">
        <svg class="tt__sun" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="4.2" />
          <path
            d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6"
          />
        </svg>
        <svg class="tt__moon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M20 13.4A8.2 8.2 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4Z" />
        </svg>
      </span>
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }

      .tt {
        display: inline-grid;
        place-items: center;
        inline-size: var(--lc-touch-min);
        block-size: var(--lc-touch-min);
        padding: 0;
        border: 1px solid var(--lc-border);
        border-radius: var(--lc-radius-pill);
        background: var(--lc-surface);
        color: var(--lc-ink-secondary);
        cursor: pointer;
        transition:
          color var(--t-fast) var(--ease-out),
          border-color var(--t-fast) var(--ease-out),
          background var(--t-fast) var(--ease-out);
      }

      .tt:hover {
        color: var(--lc-ink);
        border-color: var(--lc-primary-400);
        background: var(--lc-surface-low);
      }

      .tt:active {
        transform: scale(0.94);
      }

      /* Las dos capas se superponen: el cuadro nunca cambia de tamano. */
      .tt__icons {
        position: relative;
        display: grid;
        inline-size: 18px;
        block-size: 18px;
      }

      .tt__icons > svg {
        grid-area: 1 / 1;
        inline-size: 100%;
        block-size: 100%;
        stroke-width: 1.8;
        stroke-linecap: round;
        transition:
          opacity var(--t-base) var(--ease-out),
          transform var(--t-base) var(--ease-out);
      }

      .tt__moon {
        opacity: 0;
        transform: rotate(-70deg) scale(0.5);
      }

      .tt__icons--dark .tt__sun {
        opacity: 0;
        transform: rotate(70deg) scale(0.5);
      }

      .tt__icons--dark .tt__moon {
        opacity: 1;
        transform: none;
      }
    `,
  ],
})
export class ThemeToggleComponent {
  protected readonly theme = inject(ThemeService);

  protected readonly isDark = computed(() => this.theme.theme() === 'dark');

  protected readonly label = computed(() =>
    this.isDark() ? 'Activar modo claro' : 'Activar modo oscuro',
  );
}
